import { useEffect, useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import authApiService from "../../../services/auth-api.service";
import operatorApiService from "../../../services/operator-api.service";
import type { BasePoint, CoursePoint, VehicleOption } from "../utils/types";

type Args = {
  navigate: NavigateFunction;
};

// bpslog画面のメタ情報（ユーザ/拠点/コース/車両一覧など）を取得してstate化する。
export const useBpsLogMetadata = ({ navigate }: Args) => {
  const [vehicleId, setVehicleId] = useState<number | null>(null);
  const [vehicleOptions, setVehicleOptions] = useState<VehicleOption[]>([]);
  const [vehicleSuidById, setVehicleSuidById] = useState<
    Record<number, string>
  >({});
  const [baseId, setBaseId] = useState<number | null>(null);
  const [baseName, setBaseName] = useState<string>("");
  const [basePoint, setBasePoint] = useState<BasePoint | null>(null);
  const [coursePoints, setCoursePoints] = useState<CoursePoint[]>([]);

  useEffect(() => {
    const fetchMe = async () => {
      try {
        const user = await authApiService.me();
        if (user.role_cd !== "07") {
          navigate("/login");
          return;
        }

        setBaseId(typeof user.base_id === "number" ? user.base_id : null);
        setBaseName(typeof user.base_name === "string" ? user.base_name : "");
      } catch (error) {
        navigate("/login");
      }
    };
    fetchMe();
  }, [navigate]);

  useEffect(() => {
    if (!baseId) return;

    const fetchBase = async () => {
      try {
        const results = await operatorApiService.doGetBasePoint({ baseId });
        const point = results?.[0];
        if (point?.map_latitude && point?.map_longitude) {
          setBasePoint({
            map_latitude: Number(point.map_latitude),
            map_longitude: Number(point.map_longitude),
            map_zoom: Number(point.map_zoom ?? 14),
          });
        }
      } catch (err) {
        console.error("地図初期ポイント取得エラー:", err);
      }
    };

    const fetchCourse = async () => {
      try {
        const points = await operatorApiService.doGetMapPoints({ baseId });
        if (Array.isArray(points)) {
          setCoursePoints(points);
        } else {
          setCoursePoints([]);
        }
      } catch (err) {
        console.error("地図ピン取得エラー:", err);
        setCoursePoints([]);
      }
    };

    fetchBase();
    fetchCourse();
  }, [baseId]);

  // 車両リスト取得
  useEffect(() => {
    if (!baseId) return;

    let cancelled = false;
    const fetchVehicles = async () => {
      try {
        const vehicles = await operatorApiService.doGetVehicles({ baseId });
        if (cancelled) return;

        const nextSuidById: Record<number, string> = {};
        if (Array.isArray(vehicles)) {
          vehicles.forEach((v: any) => {
            const idRaw = v?.vehicle_id;
            const id =
              typeof idRaw === "number"
                ? idRaw
                : typeof idRaw === "string"
                  ? Number(idRaw)
                  : Number.NaN;
            if (!Number.isFinite(id)) return;
            const suid = typeof v?.zao_suid === "string" ? v.zao_suid : "";
            nextSuidById[id] = suid;
          });
        }
        setVehicleSuidById(nextSuidById);

        const options: VehicleOption[] = Array.isArray(vehicles)
          ? vehicles
              .map((v: any): VehicleOption | null => {
                const idRaw = v?.vehicle_id;
                const id =
                  typeof idRaw === "number"
                    ? idRaw
                    : typeof idRaw === "string"
                      ? Number(idRaw)
                      : Number.NaN;
                if (!Number.isFinite(id)) return null;
                const label: string =
                  (typeof v?.vehicle_label === "string" && v.vehicle_label) ||
                  (typeof v?.vehicle_no === "string" && v.vehicle_no
                    ? `${v.vehicle_no}号車`
                    : "");
                return { value: id, label: label || String(id) };
              })
              .filter((opt): opt is VehicleOption => opt !== null)
          : [];

        options.sort((a, b) => {
          return a.value - b.value;
        });

        if (options.length) {
          setVehicleOptions(options);
          setVehicleId((prev) =>
            prev !== null && options.some((o) => o.value === prev)
              ? prev
              : options[0].value,
          );
        } else {
          setVehicleOptions([]);
          setVehicleId(null);
          setVehicleSuidById({});
        }
      } catch (err) {
        if (cancelled) return;
        console.error("vehicleId list fetch error:", err);
      }
    };

    fetchVehicles();
    return () => {
      cancelled = true;
    };
  }, [baseId]);

  return {
    baseId,
    baseName,
    basePoint,
    coursePoints,
    vehicleId,
    setVehicleId,
    vehicleOptions,
    vehicleSuidById,
  };
};
